import { useCallback, useEffect, useRef, useState } from "react";
import api from "../services/api";

/**
 * Poll a GET endpoint. Returns { data, error, loading, reload }.
 * `error` is set only when no data has ever loaded, so a transient failure
 * keeps showing the last good data instead of flashing an error state.
 */
export function usePoll(url, { interval = 0, params, enabled = true, transform } = {}) {
  const [state, setState] = useState({ data: null, error: null, loading: true });
  const key = url + JSON.stringify(params || {});
  const transformRef = useRef(transform);
  transformRef.current = transform;

  const load = useCallback(async () => {
    if (!url) return;
    try {
      const r = await api.get(url, { params });
      const data = transformRef.current ? transformRef.current(r.data) : r.data;
      setState({ data, error: null, loading: false });
    } catch (e) {
      setState((s) => ({
        data: s.data,
        error: s.data ? null : e?.response?.data?.detail || e.message || "Request failed",
        loading: false,
      }));
    }
    // `key` captures url + params; params is intentionally read from the closure
    // eslint-disable-next-line
  }, [key]);

  useEffect(() => {
    if (!enabled) return undefined;
    setState((s) => ({ ...s, loading: !s.data }));
    load();
    if (!interval) return undefined;
    const id = setInterval(load, interval);
    return () => clearInterval(id);
  }, [load, interval, enabled]);

  return { ...state, reload: load };
}

/** Re-render every `ms` milliseconds; returns the current time. */
export function useNow(ms = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Stacked overlays (a confirm dialog over a drawer): only the top one handles keys.
const trapStack = [];

/** Trap focus inside `ref` while active; Escape calls onClose; focus returns on close. */
export function useFocusTrap(ref, active, onClose) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!active) return undefined;
    const token = {};
    trapStack.push(token);
    const previous = document.activeElement;
    const node = ref.current;
    const first = node?.querySelector("[data-autofocus]") || node?.querySelector(FOCUSABLE);
    (first || node)?.focus({ preventScroll: true });
    const onKey = (e) => {
      if (trapStack[trapStack.length - 1] !== token) return;
      if (e.key === "Escape") {
        e.stopPropagation();
        closeRef.current?.();
        return;
      }
      if (e.key !== "Tab" || !node) return;
      const items = [...node.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const a = items[0];
      const b = items[items.length - 1];
      if (e.shiftKey && document.activeElement === a) {
        e.preventDefault();
        b.focus();
      } else if (!e.shiftKey && document.activeElement === b) {
        e.preventDefault();
        a.focus();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      const i = trapStack.indexOf(token);
      if (i >= 0) trapStack.splice(i, 1);
      document.removeEventListener("keydown", onKey, true);
      if (previous && previous.focus) previous.focus({ preventScroll: true });
    };
  }, [active, ref]);
}

/** Lock page scroll while `active`. */
export function useScrollLock(active) {
  useEffect(() => {
    if (!active) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [active]);
}

/** Hash router: "#/page/tab" -> { page, tab }. */
export function parseHash(hash = window.location.hash) {
  const [page, tab] = hash.replace(/^#\/?/, "").split("/");
  return { page: page || "", tab: tab || "" };
}

export function useHashRoute(defaultPage) {
  const [route, setRoute] = useState(() => {
    const r = parseHash();
    return { page: r.page || defaultPage, tab: r.tab };
  });
  useEffect(() => {
    const on = () => {
      const r = parseHash();
      setRoute({ page: r.page || defaultPage, tab: r.tab });
    };
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, [defaultPage]);
  const navigate = useCallback((page, tab) => {
    const next = `#/${page}${tab ? `/${tab}` : ""}`;
    if (window.location.hash !== next) window.location.hash = next;
  }, []);
  return [route, navigate];
}

/** Keep a value in localStorage (per-viewer convenience only). */
export function useStoredState(key, initial) {
  const [v, setV] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw == null ? initial : JSON.parse(raw);
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(v));
    } catch {
      /* storage unavailable */
    }
  }, [key, v]);
  return [v, setV];
}
